import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, X, Loader2, Eye, EyeOff, Clock } from "lucide-react";
import { api, mediaUrl, formatPrice } from "@/lib/api";
import { ImageUploader } from "@/components/ImageUploader";

const types = ["Appartamento", "Villa", "Attico", "Terratetto", "Villetta a schiera", "Commerciale", "Terreno"];
const contracts = ["Vendita", "Affitto"];
const zones = ["Scandicci Centro", "Casellina", "Vingone", "Le Bagnese", "Firenze Isolotto", "Firenze Centro", "Firenze San Frediano", "Colline / Chianti", "Altro"];
const energy = ["A4", "A3", "A2", "A1", "B", "C", "D", "E", "F", "G"];
const statusOpts = ["Disponibile", "Riservato", "Non più disponibile"];

const empty = { title: "", type: types[0], contract: "Vendita", zone: zones[0], price: "", rooms: "", bathrooms: "", surface: "", energy_class: "G", address: "", description: "", images: [], floorplans: [], status: "Disponibile", featured: false, published: true, meta_title: "", meta_description: "" };
const field = "w-full px-4 py-2.5 rounded-xl border border-[#DCE6EF] bg-white focus:outline-none focus:ring-2 focus:ring-[#2472C8]/40";

const UNAVAILABLE = ["Venduto", "Affittato", "Non più disponibile"];
const statusChip = { Disponibile: "bg-[#166534]", Riservato: "bg-[#854D0E]" };
const archiveRemaining = (p) => {
  const since = p.unavailable_since ? new Date(p.unavailable_since) : new Date();
  const elapsed = Math.floor((Date.now() - since.getTime()) / 86400000);
  return 120 - elapsed;
};
const rank = (p) => (!p.published ? 2 : (UNAVAILABLE.includes(p.status) ? 1 : 0));

export default function AdminProperties() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null); // null | {} | property
  const { data: properties = [] } = useQuery({ queryKey: ["admin-properties"], queryFn: async () => (await api.get("/admin/properties")).data });

  const remove = async (id) => {
    if (!window.confirm("Eliminare questo immobile?")) return;
    await api.delete(`/admin/properties/${id}`);
    qc.invalidateQueries({ queryKey: ["admin-properties"] });
    toast.success("Immobile eliminato");
  };

  return (
    <div>
      <div className="flex items-center justify-between flex-wrap gap-4">
        <h1 className="font-display text-3xl font-bold text-[#1B2A41]">Immobili</h1>
        <button onClick={() => setEditing({ ...empty })} className="bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold px-5 py-2.5 rounded-full flex items-center gap-2 transition-colors" data-testid="add-property-btn"><Plus className="w-5 h-5" /> Nuovo immobile</button>
      </div>

      <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {[...properties].sort((a, b) => rank(a) - rank(b)).map((p) => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#DCE6EF] overflow-hidden" data-testid={`admin-property-${p.id}`}>
            <div className="aspect-video bg-[#E3ECF3] relative">
              {p.images?.[0] ? <img src={mediaUrl(p.images[0].url)} alt={p.title} className={`w-full h-full object-cover ${UNAVAILABLE.includes(p.status) ? "grayscale opacity-70" : ""}`} /> : <div className="w-full h-full flex items-center justify-center text-[#A9B4C1] text-sm font-mono">Nessuna foto</div>}
              <div className="absolute top-2 left-2 flex flex-wrap gap-1">
                {p.featured && <span className="bg-[#E0961C] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">IN VETRINA</span>}
                {p.published
                  ? <span className="bg-[#166534] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">PUBBLICO</span>
                  : <span className={`text-white text-[10px] font-bold px-2 py-0.5 rounded-full ${UNAVAILABLE.includes(p.status) ? "bg-[#1B2A41]" : "bg-[#566476]"}`}>{UNAVAILABLE.includes(p.status) ? "ARCHIVIATO" : "BOZZA"}</span>}
                {UNAVAILABLE.includes(p.status) && <span className="bg-[#7A1F1F] text-white text-[10px] font-bold px-2 py-0.5 rounded-full">NON PIÙ DISPONIBILE</span>}
              </div>
            </div>
            <div className="p-4">
              <div className="text-xs font-mono text-[#2472C8]">{p.zone} · {p.contract}</div>
              <h3 className="font-heading font-semibold text-[#1B2A41] mt-1 line-clamp-1">{p.title}</h3>
              <div className="font-mono font-semibold text-[#1B2A41] mt-1">{formatPrice(p.price)}</div>
              {UNAVAILABLE.includes(p.status) && p.published && (
                <div className="mt-2 flex items-center gap-1.5 text-[11px] font-medium text-[#B4531F] bg-[#FBF3E7] rounded-md px-2 py-1" data-testid={`archive-note-${p.id}`}>
                  <Clock className="w-3.5 h-3.5 shrink-0" />
                  {archiveRemaining(p) > 0 ? `Archiviazione automatica tra ${archiveRemaining(p)} giorni` : "In archiviazione a breve"}
                </div>
              )}
              {UNAVAILABLE.includes(p.status) && !p.published && (
                <div className="mt-2 text-[11px] font-medium text-[#566476] bg-[#EEF4F9] rounded-md px-2 py-1" data-testid={`archived-note-${p.id}`}>Archiviato · non più visibile sul sito</div>
              )}
              <div className="flex gap-2 mt-4">
                <button onClick={() => setEditing(p)} className="flex-1 bg-[#1B2A41] text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1" data-testid={`edit-property-${p.id}`}><Pencil className="w-4 h-4" /> Modifica</button>
                <button onClick={() => remove(p.id)} className="px-3 border border-[#DCE6EF] text-[#7A1F1F] rounded-lg hover:bg-red-50" data-testid={`delete-property-${p.id}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
        {!properties.length && <div className="col-span-full bg-white rounded-2xl border border-dashed border-[#CBD7E2] p-12 text-center text-[#8695A6]">Nessun immobile. Clicca &laquo;Nuovo immobile&raquo; per iniziare.</div>}
      </div>

      {editing && <PropertyEditor initial={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ["admin-properties"] }); qc.invalidateQueries({ queryKey: ["stats"] }); }} />}
    </div>
  );
}

function PropertyEditor({ initial, onClose, onSaved }) {
  const [f, setF] = useState(() => {
    const init = { ...empty, ...initial };
    if (init.status === "Venduto" || init.status === "Affittato") init.status = "Non più disponibile";
    return init;
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async () => {
    if (!f.title.trim()) return toast.error("Il titolo è obbligatorio");
    setSaving(true);
    const payload = {
      ...f,
      price: f.price === "" ? null : Number(f.price),
      rooms: f.rooms === "" ? null : Number(f.rooms),
      bathrooms: f.bathrooms === "" ? null : Number(f.bathrooms),
      surface: f.surface === "" ? null : Number(f.surface),
    };
    try {
      if (f.id) await api.put(`/admin/properties/${f.id}`, payload);
      else await api.post("/admin/properties", payload);
      toast.success("Immobile salvato");
      onSaved();
    } catch (e) {
      toast.error("Errore nel salvataggio");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-0 sm:p-4" data-testid="property-editor">
      <div className="bg-[#F7FAFC] w-full h-full sm:h-auto sm:max-w-3xl sm:rounded-2xl flex flex-col max-h-[100dvh] sm:max-h-[90vh] overflow-hidden">
        <div className="shrink-0 bg-[#1B2A41] text-white px-6 py-4 flex items-center justify-between sm:rounded-t-2xl">
          <h2 className="font-display text-xl font-bold">{f.id ? "Modifica immobile" : "Nuovo immobile"}</h2>
          <button onClick={onClose} data-testid="editor-close"><X className="w-6 h-6" /></button>
        </div>
        <div className="flex-1 overflow-y-auto p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-[#1B2A41] mb-2">Titolo *</label>
            <input value={f.title} onChange={set("title")} placeholder="Es. Trilocale ristrutturato Scandicci Centro" className={field} data-testid="prop-title" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium mb-2">Tipologia</label><select value={f.type} onChange={set("type")} className={field} data-testid="prop-type">{types.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-2">Contratto</label><select value={f.contract} onChange={set("contract")} className={field} data-testid="prop-contract">{contracts.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-2">Zona</label><select value={f.zone} onChange={set("zone")} className={field} data-testid="prop-zone">{zones.map((t) => <option key={t}>{t}</option>)}</select></div>
          </div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div><label className="block text-sm font-medium mb-2">Prezzo €</label><input type="number" value={f.price} onChange={set("price")} className={field} data-testid="prop-price" /></div>
            <div><label className="block text-sm font-medium mb-2">Locali</label><input type="number" value={f.rooms} onChange={set("rooms")} className={field} data-testid="prop-rooms" /></div>
            <div><label className="block text-sm font-medium mb-2">Bagni</label><input type="number" value={f.bathrooms} onChange={set("bathrooms")} className={field} data-testid="prop-bathrooms" /></div>
            <div><label className="block text-sm font-medium mb-2">Superficie m²</label><input type="number" value={f.surface} onChange={set("surface")} className={field} data-testid="prop-surface" /></div>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <div><label className="block text-sm font-medium mb-2">Classe energetica</label><select value={f.energy_class} onChange={set("energy_class")} className={field}>{energy.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-2">Stato</label><select value={f.status} onChange={set("status")} className={field}>{statusOpts.map((t) => <option key={t}>{t}</option>)}</select></div>
            <div><label className="block text-sm font-medium mb-2">Indirizzo</label><input value={f.address} onChange={set("address")} className={field} /></div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Descrizione</label>
            <textarea value={f.description} onChange={set("description")} rows={5} className={field} data-testid="prop-description" />
          </div>
          <ImageUploader value={f.images} onChange={(v) => setF({ ...f, images: v })} label="Foto immobile" testid="prop-images" />
          <ImageUploader value={f.floorplans} onChange={(v) => setF({ ...f, floorplans: v })} label="Planimetrie" testid="prop-floorplans" />
          <div className="flex gap-6">
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.featured} onChange={(e) => setF({ ...f, featured: e.target.checked })} className="accent-[#2472C8]" data-testid="prop-featured" /> In vetrina</label>
            <label className="flex items-center gap-2 cursor-pointer"><input type="checkbox" checked={f.published} onChange={(e) => setF({ ...f, published: e.target.checked })} className="accent-[#2472C8]" data-testid="prop-published" /> {f.published ? <><Eye className="w-4 h-4" /> Pubblicato</> : <><EyeOff className="w-4 h-4" /> Bozza</>}</label>
          </div>
          <div className="grid sm:grid-cols-2 gap-4 pt-2 border-t border-[#DCE6EF]">
            <div><label className="block text-sm font-medium mb-2 mt-2">Meta title (SEO)</label><input value={f.meta_title} onChange={set("meta_title")} className={field} /></div>
            <div><label className="block text-sm font-medium mb-2 mt-2">Meta description (SEO)</label><input value={f.meta_description} onChange={set("meta_description")} className={field} /></div>
          </div>
        </div>
        <div className="shrink-0 bg-[#F7FAFC] border-t border-[#DCE6EF] px-6 py-4 flex justify-end gap-3 sm:rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 rounded-full border border-[#DCE6EF] font-semibold text-[#1B2A41]">Annulla</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-full bg-[#2472C8] hover:bg-[#1E5FA6] text-white font-semibold flex items-center gap-2 disabled:opacity-60" data-testid="save-property-btn">{saving && <Loader2 className="w-4 h-4 animate-spin" />} Salva immobile</button>
        </div>
      </div>
    </div>
  );
}
